import { useEffect, useState, type DragEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "../components/streaming-ui";
import { Callout, EmptyState, Page, PageHeader } from "../components/ui";
import { api } from "../api";
import type { LibraryItem, Playlist, PlaylistWithItems, PlaylistItemEnriched, JingleWithLibrary } from "../types";

function formatDuration(ms: number | null): string {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

const SLOT_HOTKEYS = [
  "F1", "F2", "F3", "F4", "F5", "F6",
  "F7", "F8", "F9", "F10", "F11", "F12",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "Q", "W",
];

export function PlayoutPage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const tab = "playout" as const;
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [activePlaylist, setActivePlaylist] = useState<PlaylistWithItems | null>(null);
  const [jingles, setJingles] = useState<JingleWithLibrary[]>([]);
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      api.listPlaylists(slug).catch(() => []),
      api.listJingles(slug).catch(() => []),
      api.listLibrary({ limit: 200 }).catch(() => []),
    ]).then(([pls, js, lib]) => {
      setPlaylists(pls);
      setJingles(js);
      setLibrary(lib);
      if (pls.length > 0) {
        api.getPlaylist(pls[0].id).then(setActivePlaylist).catch(() => setActivePlaylist(null));
      }
    });
  }, [slug]);

  const onSelectPlaylist = async (pl: Playlist): Promise<void> => {
    setBusy(true);
    try {
      const detail = await api.getPlaylist(pl.id);
      setActivePlaylist(detail);
    } finally {
      setBusy(false);
    }
  };

  const onCreatePlaylist = async (): Promise<void> => {
    const name = prompt("Playlist-Name?");
    if (!name) return;
    const slug = name.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
    try {
      const pl = await api.createPlaylist(window.location.pathname.split("/").pop() ?? "", {
        name,
        slug,
        rotation_mode: "sequential",
        default_crossfade_ms: 2000,
      });
      setPlaylists([...playlists, pl]);
      onSelectPlaylist(pl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onSaveItems = async (newItems: PlaylistItemEnriched[]): Promise<void> => {
    if (!activePlaylist) return;
    setBusy(true);
    try {
      await api.setPlaylistItems(
        activePlaylist.id,
        newItems.map((it, idx) => ({
          item_id: it.item_id,
          position: idx,
          fade_in_ms: it.fade_in_ms,
          fade_out_ms: it.fade_out_ms,
        }))
      );
      const detail = await api.getPlaylist(activePlaylist.id);
      setActivePlaylist(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onAddToPlaylist = async (itemId: string): Promise<void> => {
    if (!activePlaylist) return;
    const newItems = [...activePlaylist.items, {
      ...(activePlaylist.items[0] ?? {
        id: "",
        playlist_id: activePlaylist.id,
        position: 0,
        fade_in_ms: 0,
        fade_out_ms: 0,
        skip_ratio: null,
        created_at: new Date().toISOString(),
      }),
      item_id: itemId,
      position: activePlaylist.items.length,
      title: library.find((l) => l.id === itemId)?.title ?? "Unknown",
      artist: library.find((l) => l.id === itemId)?.artist ?? null,
      duration_ms: library.find((l) => l.id === itemId)?.duration_ms ?? null,
    } as PlaylistItemEnriched];
    await onSaveItems(newItems);
  };

  const onMoveItem = async (from: number, to: number): Promise<void> => {
    if (!activePlaylist) return;
    if (to < 0 || to >= activePlaylist.items.length) return;
    const items = [...activePlaylist.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    await onSaveItems(items);
  };

  const onRemoveItem = async (index: number): Promise<void> => {
    if (!activePlaylist) return;
    const items = activePlaylist.items.filter((_, i) => i !== index);
    await onSaveItems(items);
  };

  const onAddJingle = async (slotIndex: number): Promise<void> => {
    const name = prompt("Jingle-Name?");
    if (!name) return;
    const itemId = library[0]?.id;
    if (!itemId) {
      setError("Kein Library-Item vorhanden – erst Audio hochladen");
      return;
    }
    try {
      const j = await api.createJingle(slug, {
        name,
        slot_index: slotIndex,
        library_item_id: itemId,
        hotkey: SLOT_HOTKEYS[slotIndex] ?? undefined,
        color: "#ff5500",
      });
      setJingles([...jingles.filter((x) => x.slot_index !== slotIndex), j].sort((a, b) => a.slot_index - b.slot_index));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onRemoveJingle = async (id: string): Promise<void> => {
    try {
      await api.deleteJingle(id);
      setJingles(jingles.filter((j) => j.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const belegt = jingles.length;

  return (
    <Page className="max-w-7xl">
      <PageHeader
        eyebrow={<Link to={`/channels/${slug}`} className="hover:text-brand-primary">Kanal · {slug}</Link>}
        title="Playlists & Cartwall"
        description="Playlisten für die Automatik und die Cartwall mit 24 Jingle-Plätzen."
        actions={
          tab === "playout" && (
            <button onClick={onCreatePlaylist} className="btn btn-primary">
              <Icon name="plus" /> Playlist
            </button>
          )
        }
      />

      <div className="space-y-4">
      {error && <Callout tone="danger">{error}</Callout>}

      {tab === "playout" && (
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-5">
        {/* Playlisten */}
        <section className="card min-w-0 xl:col-span-3">
          <div className="card-header flex-wrap">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <h2 className="card-title">Playlist</h2>
              <select
                className="input !h-8 !w-auto max-w-[16rem] !text-sm"
                value={activePlaylist?.id ?? ""}
                onChange={(e) => {
                  const pl = playlists.find((p) => p.id === e.target.value);
                  if (pl) onSelectPlaylist(pl);
                }}
                disabled={playlists.length === 0}
              >
                {playlists.length === 0 && <option>Keine Playlist</option>}
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.rotation_mode})
                  </option>
                ))}
              </select>
            </div>
            {activePlaylist && (
              <span className="flex items-center gap-2 text-xs text-muted">
                <span className="badge badge-neutral tabular-nums">{activePlaylist.items.length} Items</span>
                <span className="inline-flex items-center gap-1 font-mono tabular-nums"><Icon name="clock" className="h-3.5 w-3.5" /> {formatDuration(activePlaylist.total_duration_ms)}</span>
              </span>
            )}
          </div>

          <div className="card-body">
          {activePlaylist ? (
            <PlaylistEditor
              items={activePlaylist.items}
              library={library}
              onAdd={onAddToPlaylist}
              onMove={onMoveItem}
              onRemove={onRemoveItem}
              busy={busy}
            />
          ) : (
            <EmptyState icon={<Icon name="music" />} title="Keine Playlist ausgewählt." description="Lege oben rechts eine Playlist an oder wähle eine aus." />
          )}
          </div>
        </section>

        {/* Cartwall */}
        <section className="card min-w-0 xl:col-span-2">
          <div className="card-header">
            <h2 className="card-title">Cartwall</h2>
            <span className="badge badge-neutral tabular-nums">{belegt} / 24 belegt</span>
          </div>
          <div className="card-body">
          <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
            {Array.from({ length: 24 }).map((_, idx) => {
              const j = jingles.find((x) => x.slot_index === idx);
              const hotkey = SLOT_HOTKEYS[idx];
              return (
                <div
                  key={idx}
                  className={`group relative aspect-square overflow-hidden rounded-xl p-2 text-xs transition-all ${
                    j ? "text-white hover:-translate-y-px" : "border border-dashed border-[var(--border-strong)] bg-[var(--bg-sunken)] hover:border-brand-primary/60 hover:bg-brand-primary/[0.04]"
                  }`}
                  style={
                    j
                      ? {
                          background: `linear-gradient(155deg, ${j.color} 0%, ${j.color}cc 55%, ${j.color}99 100%)`,
                          boxShadow: `0 0 0 1px ${j.color}, 0 10px 22px -10px ${j.color}, inset 0 1px 0 rgba(255,255,255,0.25)`,
                        }
                      : undefined
                  }
                >
                  <div className={`absolute right-1.5 top-1.5 rounded-md px-1 font-mono text-[10px] font-semibold ${j ? "bg-black/25 text-white" : "bg-card text-muted ring-1 ring-[var(--border)]"}`}>{hotkey}</div>
                  <div className={`absolute left-1.5 top-1.5 font-mono text-[10px] tabular-nums ${j ? "text-white/75" : "text-muted"}`}>#{idx}</div>
                  {j ? (
                    <div className="flex h-full flex-col justify-end">
                      <div className="line-clamp-2 text-[0.72rem] font-semibold leading-tight drop-shadow-sm" title={j.name}>{j.name}</div>
                      <div className="mt-0.5 flex items-center justify-between">
                        <span className="font-mono text-[10px] tabular-nums text-white/85">{formatDuration(j.library_item.duration_ms)}</span>
                        <button
                          onClick={() => onRemoveJingle(j.id)}
                          className="flex h-5 w-5 items-center justify-center rounded-md bg-black/20 text-white opacity-80 hover:bg-black/40 hover:opacity-100"
                          title="Jingle entfernen"
                          aria-label="Jingle entfernen"
                        >
                          <Icon name="x" className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => onAddJingle(idx)}
                      className="flex h-full w-full items-center justify-center text-muted group-hover:text-brand-primary"
                      aria-label={`Jingle auf Platz ${idx} legen`}
                    >
                      <Icon name="plus" className="h-5 w-5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-muted">
            Hinweis: Cartwall ist UI-Vorbereitung. Jingle-Trigger via HTTP /trigger/jingle/&lt;slot&gt; ist im Liquidsoap-Generator vorbereitet.
          </p>
          </div>
        </section>
        </div>
      )}
      </div>
    </Page>
  );
}

/** Platzhalter-Cover: Farbe aus dem Titel, damit gleiche Titel gleich aussehen. */
function CoverTile({ title }: { title: string }): JSX.Element {
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) % 360;
  return (
    <span
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-xs font-bold text-white shadow-sm"
      style={{ background: `linear-gradient(135deg, hsl(${h} 65% 55%), hsl(${(h + 40) % 360} 70% 40%))` }}
      aria-hidden="true"
    >
      {title.slice(0, 1).toUpperCase()}
    </span>
  );
}

function PlaylistEditor({
  items,
  library,
  onAdd,
  onMove,
  onRemove,
  busy,
}: {
  items: PlaylistItemEnriched[];
  library: LibraryItem[];
  onAdd: (itemId: string) => void;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
  busy: boolean;
}): JSX.Element {
  const [addingId, setAddingId] = useState<string>("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const onDrop = (e: DragEvent<HTMLLIElement>, idx: number): void => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    onMove(dragIdx, idx);
    setDragIdx(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input min-w-0 flex-1 basis-56"
          value={addingId}
          onChange={(e) => setAddingId(e.target.value)}
        >
          <option value="">+ Item hinzufügen</option>
          {library.map((l) => (
            <option key={l.id} value={l.id}>
              {l.title} · {l.artist ?? "—"} ({formatDuration(l.duration_ms)})
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            if (addingId) {
              onAdd(addingId);
              setAddingId("");
            }
          }}
          disabled={!addingId || busy}
          className="btn btn-primary"
        >
          Hinzufügen
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-default px-4 py-10 text-center text-sm text-muted">
          Diese Playlist ist leer. Titel oben aus der Mediathek hinzufügen.
        </div>
      ) : (
      <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-default">
        {items.map((it, idx) => (
          <li
            key={it.id || `${it.item_id}-${idx}`}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, idx)}
            className={`group flex min-w-0 items-center gap-3 bg-card px-2.5 py-2 text-sm hover:bg-[var(--bg-hover)] ${dragIdx === idx ? "opacity-50" : ""}`}
          >
            <span className="cursor-grab text-muted opacity-50 group-hover:opacity-100" aria-hidden="true"><Icon name="grip" /></span>
            <span className="w-5 text-right font-mono text-xs tabular-nums text-muted">{idx + 1}</span>
            <CoverTile title={it.title} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-primary">{it.title}</div>
              <div className="truncate text-xs text-muted">
                {it.artist ?? "—"}
                <span className="hidden sm:inline"> · fade in {it.fade_in_ms}ms / out {it.fade_out_ms}ms</span>
              </div>
            </div>
            <span className="font-mono text-xs tabular-nums text-secondary">{formatDuration(it.duration_ms)}</span>
            <div className="flex items-center">
              <button
                onClick={() => onMove(idx, idx - 1)}
                disabled={idx === 0 || busy}
                className="btn btn-ghost btn-sm !w-7 !px-0"
                aria-label="nach oben"
              >
                <Icon name="up" className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onMove(idx, idx + 1)}
                disabled={idx === items.length - 1 || busy}
                className="btn btn-ghost btn-sm !w-7 !px-0"
                aria-label="nach unten"
              >
                <Icon name="down" className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onRemove(idx)}
                disabled={busy}
                className="btn btn-ghost btn-sm !w-7 !px-0 text-red-600 dark:text-red-400"
                aria-label="entfernen"
              >
                <Icon name="x" className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      )}
    </div>
  );
}
